import React, { useState, useMemo } from 'react';
// kojiru-build: landing-v0.3.0 (GENIUS Act framing canonical)
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import {
  ArrowRight, Shield, Home, DollarSign, Percent, TrendingDown, TrendingUp,
  Calculator, Bot, PiggyBank, UserCheck, ChevronRight,
  Heart, Clock, Scale, Building2, FileText, Zap, Award,
  BarChart3, CheckCircle, ArrowUpRight, Umbrella, Coins,
  Users, Lock, Activity, BookOpen, Layers, Vote, Flame,
  ExternalLink, Globe, Share2, RotateCcw, RefreshCw, ChevronDown, Rocket, Map, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import HonestStatusBar from '@/components/HonestStatusBar';
import AgentEcosystemBar from '@/components/AgentEcosystemBar';


/* ── Roadmap Section ── */
function RoadmapSection() {
  const [openPhase, setOpenPhase] = useState(null);

  const phases = [
    {
      id: 'q2-2026',
      phase: 'Q2 2026',
      label: 'Foundation',
      status: 'In Progress',
      statusColor: 'bg-emerald-500',
      color: 'emerald',
      icon: Zap,
      items: [
        { text: 'Complete security audit & formal verification (4 formal-verification spec drafts in repo)', done: false },
        { text: 'Launch public marketing website (kojiru.com)', done: true },
        { text: 'Publish litepaper & pin to social channels', done: true },
        { text: 'Community building: Telegram, Discord, Medium', done: false },
        { text: 'Initiate first strategic partnerships (TBA)', done: false },
      ]
    },
    {
      id: 'q3q4-2026',
      phase: 'Q3-Q4 2026',
      label: '6-Month Growth',
      status: 'Upcoming',
      statusColor: 'bg-blue-500',
      color: 'blue',
      icon: Rocket,
      subtitle: 'Kojiru is excited about three market opportunities and is eager to accelerate growth in the remaining two segments of its business model!',
      items: [
        { text: 'Token Generation Event (TGE) & initial distribution', done: false },
        { text: 'Pursue exchange listing as a milestone goal', done: false },
        { text: 'Onboard first cohort of institutional lenders', done: false },
        { text: 'Launch live agent credit marketplace', done: false },
        { text: 'Chainlink credit oracle integration', done: false },
        { text: 'Cross-chain credit portability', done: false },
      ]
    },
    {
      id: '2027',
      phase: '2027',
      label: '1-Year Scale',
      status: 'Planned',
      statusColor: 'bg-purple-500',
      color: 'purple',
      icon: Globe,
      subtitle: 'Scale the Agent Credit Protocol across chains and frameworks.',
      items: [
        { text: 'Full production credit marketplace', done: false },
        { text: 'Cross-chain deployment to additional L2 networks', done: false },
        { text: 'Strategic partnerships with AI agent frameworks', done: false },
        { text: 'Mobile application launch (iOS + Android)', done: false },
        { text: 'Target $100M+ in total credit origination volume', done: false },
      ]
    },
  ];

  const toggle = (id) => setOpenPhase(openPhase === id ? null : id);

  return (
    <section className="py-24" data-testid="roadmap-section">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <span className="text-sm font-medium text-amber-400 tracking-widest uppercase mb-3 block">Roadmap</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Where We're{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-300 bg-clip-text text-transparent">Going</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto">
            A clear path from launch to scale. Click each phase to see the details.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {phases.map((p, i) => {
            const isOpen = openPhase === p.id;
            const doneCount = p.items.filter(item => item.done).length;

            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <button
                  onClick={() => toggle(p.id)}
                  data-testid={`roadmap-toggle-${p.id}`}
                  className={`w-full text-left p-5 rounded-2xl border transition-all ${
                    isOpen
                      ? `bg-${p.color}-500/10 border-${p.color}-500/30`
                      : 'bg-white/[0.02] border-white/[0.06] hover:border-white/15'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl bg-${p.color}-500/15 flex items-center justify-center`}>
                        <p.icon className={`w-5 h-5 text-${p.color}-400`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-white">{p.phase}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.statusColor}/20 text-${p.color}-400 font-medium`}>
                            {p.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">{p.label}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {doneCount > 0 && (
                        <span className="text-xs text-emerald-400 font-medium">{doneCount}/{p.items.length} done</span>
                      )}
                      <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-2 ml-7 pl-7 border-l border-white/[0.06] space-y-2.5 py-3"
                  >
                    {p.subtitle && (
                      <p className="text-sm text-gray-400 italic mb-3 pb-3 border-b border-white/[0.06]">{p.subtitle}</p>
                    )}
                    {p.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                          item.done
                            ? 'bg-emerald-500 border-emerald-500'
                            : `border-${p.color}-500/30`
                        }`}>
                          {item.done && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className={`text-sm ${item.done ? 'text-gray-300' : 'text-gray-500'}`}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="max-w-3xl mx-auto mt-8">
          <div className="flex justify-between text-[10px] text-gray-600 mb-1.5">
            <span>Overall Progress</span>
            <span>{phases.reduce((s, p) => s + p.items.filter(i => i.done).length, 0)}/{phases.reduce((s, p) => s + p.items.length, 0)} milestones</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
              style={{ width: `${(phases.reduce((s, p) => s + p.items.filter(i => i.done).length, 0) / phases.reduce((s, p) => s + p.items.length, 0)) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ MAIN PAGE ══════════════════════════ */
export default function LandingV2() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet>
        <title>Kojiru: The Agent Credit Protocol | Credit Infrastructure for AI Agents</title>
        <meta name="description" content="Kojiru is the credit infrastructure layer for autonomous AI agents. Agent Credit Scoring, bilateral credit marketplace, verified escrow, and BaseScan source-verified smart contracts on Base." />
        <meta property="og:title" content="Kojiru: The Agent Credit Protocol" />
        <meta property="og:description" content="Allocating liability is the fourth pillar of agentic trust standards. Kojiru is the composable, non-custodial recourse primitive, live on Base mainnet since June 2026." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kojiru: The Agent Credit Protocol" />
        <meta name="twitter:description" content="Credit infrastructure for autonomous AI agents. Bilateral credit lines. BaseScan source-verified on Base." />
      </Helmet>
      <Navbar />

      {/* ─── HERO ─── */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-20" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/6 w-[500px] h-[500px] bg-emerald-600/[0.06] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-teal-600/[0.04] rounded-full blur-[120px]" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <Bot className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">The Recourse Layer · The Fourth Pillar of Agentic Trust</span>
              </div>

              {/* Role-picker chips: routes first-time visitors to their audience page */}
              <div className="mb-6" data-testid="role-picker">
                <div className="text-[10px] font-bold tracking-[2px] text-slate-500 uppercase mb-2">First time here? Pick your role</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "I'm a Bank", path: '/for-banks', testid: 'role-bank' },
                    { label: "I'm a Merchant", path: '/for-merchants', testid: 'role-merchant' },
                    { label: "I'm an Issuing Program", path: '/for-issuers', testid: 'role-issuer' },
                    { label: "I'm an Underwriter", path: '/for-underwriters', testid: 'role-underwriter' },
                    { label: "I'm a Payments Platform", path: '/for-payments-platforms', testid: 'role-platform' },
                  ].map((chip) => (
                    <a
                      key={chip.testid}
                      href={chip.path}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-emerald-500/15 hover:border-emerald-500/40 text-xs font-bold text-slate-200 hover:text-white transition-colors"
                      data-testid={chip.testid}
                    >
                      {chip.label} <span className="text-emerald-400">→</span>
                    </a>
                  ))}
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-5 tracking-tight" data-testid="hero-heading">
                Allocating{' '}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  Liability.
                </span>
                <br />
                <span className="text-gray-300">The Fourth Pillar.</span>
              </h1>

              <p className="text-base sm:text-lg text-gray-400 max-w-xl mb-5 leading-relaxed" data-testid="hero-subhead">
                Networks own agent identity. Standards bodies own consumer consent. Mandate schemas own delegated authority. The fourth pillar, allocating liability, is unclaimed. Kojiru is the composable, non-custodial recourse primitive that networks, issuers, processors, acquirers, insurers, and regulators each compose with. Live on Base mainnet since June 2026.
              </p>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mb-6 text-sm max-w-xl" data-testid="hero-stat-strip">
                <span className="flex items-center gap-1.5">
                  <span className="font-mono font-bold text-emerald-400">+38 pts</span>
                  <span className="text-gray-500">consumer trust when a recourse guarantor is named</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="font-mono font-bold text-emerald-400">96%</span>
                  <span className="text-gray-500">of acquirers rank agent governance important</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="font-mono font-bold text-emerald-400">54%</span>
                  <span className="text-gray-500">assign refund liability to the agent builder</span>
                </span>
              </div>

              <p className="text-lg sm:text-xl font-semibold text-white mb-2 tracking-wide">
                The Agent Credit Protocol.
              </p>

              <p className="text-base sm:text-lg font-medium mb-8 tracking-wide" data-testid="hero-trademark-line">
                Featuring{' '}
                <span className="text-white font-semibold">Agent Credit Score</span>{' '}
                <span className="text-emerald-300 font-bold">
                  (ACS)<sup className="text-xs align-super ml-0.5">™</sup>
                </span>
                <span className="block text-xs text-gray-500 mt-2 tracking-normal font-normal">
                  AGENT CREDIT SCORE<sup className="text-[9px] align-super">™</sup> and (ACS)<sup className="text-[9px] align-super">™</sup> are trademarks of Kojiru.
                </span>
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Button
                  data-testid="hero-get-started"
                  onClick={() => navigate('/agent-credit')}
                  className="h-13 px-8 text-base font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl"
                >
                  Explore the Protocol <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Link
                  to="/manifesto"
                  data-testid="hero-watch-manifesto"
                  className="group inline-flex items-center h-13 px-8 text-base font-semibold rounded-xl border border-white/20 bg-white text-black hover:bg-slate-100 transition-colors"
                  title="Watch the 45-second Recourse Manifesto: give the consumers what they want"
                >
                  <span className="w-0 h-0 border-l-[8px] border-l-black border-y-[6px] border-y-transparent mr-2.5" />
                  Watch the manifesto · 45 sec
                </Link>
                <Button
                  data-testid="hero-prequalify"
                  onClick={() => navigate('/agents')}
                  variant="outline"
                  className="h-13 px-8 text-base font-semibold border-white/15 hover:bg-white/5 rounded-xl"
                >
                  Try the AI Agents <Bot className="ml-2 w-4 h-4" />
                </Button>
                <a
                  href="https://credit.kojiru.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-live-dashboard"
                  className="group inline-flex items-center h-13 px-8 text-base font-semibold rounded-xl border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-400/60 transition-colors"
                  title="Live protocol state: every claim on kojiru.com backed by public on-chain reads"
                >
                  <span className="relative flex w-2 h-2 mr-2.5">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 animate-ping"></span>
                    <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400"></span>
                  </span>
                  Live Dashboard
                  <ExternalLink className="ml-2 w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>

              <Link to="/litepaper" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-400 transition-colors" data-testid="hero-litepaper-link">
                <FileText className="w-4 h-4" /> Read Our Litepaper <ArrowRight className="w-3 h-3" />
              </Link>

              {/* Trust row */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" /> BaseScan Source-Verified</span>
                <span className="flex items-center gap-1.5"><Bot className="w-4 h-4 text-emerald-500" /> 3 Live AI Agents</span>
                <span className="flex items-center gap-1.5"><Scale className="w-4 h-4 text-emerald-500" /> GENIUS Act Aligned</span>
                <a
                  href="https://base.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-gray-500 hover:text-sky-400 transition-colors"
                  data-testid="hero-base-builder-link"
                  title="Kojiru is a Base Builder: transactions attributed via ERC-8021"
                >
                  {/* Base official logo mark (simple circle with tangent) */}
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-sky-500" aria-hidden="true">
                    <span className="block w-[11px] h-[1.5px] bg-[#0A0C14] translate-x-[0.5px]" />
                  </span>
                  Base Builder
                </a>
              </div>
            </motion.div>

            {/* Right: Protocol Overview */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
            >
              <div className="w-full max-w-lg rounded-3xl bg-[#0d1117] border border-white/[0.06] p-7 space-y-5" data-testid="hero-protocol-card">
                <div className="text-center">
                  <p className="text-xs text-emerald-400 uppercase tracking-widest font-medium mb-2">Agent Credit Protocol</p>
                  <p className="text-4xl font-bold text-white">Direct Credit</p>
                  <p className="text-sm text-gray-500 mt-1">Bilateral lender-to-agent credit lines</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Mainnet Contracts', value: '8', color: 'text-emerald-400' },
                    { label: 'BaseScan Verified', value: '8/8', color: 'text-teal-400' },
                    { label: 'AI Knowledge Bases', value: '9', color: 'text-blue-400' },
                    { label: 'Live AI Agents', value: '3', color: 'text-purple-400' },
                  ].map(s => (
                    <div key={s.label} className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.05] text-center">
                      <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2.5">
                  {[
                    { step: '1', label: 'Agent registers with on-chain identity (ERC-8004)' },
                    { step: '2', label: 'Lender evaluates agent and extends a direct credit line' },
                    { step: '3', label: 'Agent draws credit into isolated per-task escrow' },
                    { step: '4', label: 'Task completed, verified, settled: lender earns service fee' },
                  ].map(s => (
                    <div key={s.step} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] font-bold text-emerald-400">{s.step}</span>
                      </div>
                      <p className="text-sm text-gray-400">{s.label}</p>
                    </div>
                  ))}
                </div>

                <Button onClick={() => window.location.href='/agent-credit'} className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-base font-semibold rounded-xl" data-testid="hero-full-breakdown">
                  Explore Protocol <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── HONEST STATUS BAR (truth widget, fed by /app/memory/MARKETING_FACTS.md) ─── */}
      <HonestStatusBar />

      {/* Compliance handoff: bridges Honest Status pills → /compliance for risk/audit readers */}
      <section className="py-8 px-6 border-y border-white/[0.03] bg-white/[0.01]" data-testid="compliance-handoff-section">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-sm sm:text-base text-gray-400">
            Reading this as a risk, audit, or compliance function?{' '}
            <Link
              to="/compliance"
              className="text-emerald-400 hover:text-emerald-300 font-semibold inline-flex items-center gap-1.5 group"
              data-testid="compliance-handoff-link"
            >
              See how each pill maps to your team&rsquo;s questions
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </p>
        </div>
      </section>

      {/* ─── AGENT ECOSYSTEM BAR: MCP Registry + npm packages ─── */}
      <AgentEcosystemBar />

      {/* ─── SOCIAL PROOF NUMBERS ─── */}
      <section className="py-16 border-y border-white/[0.04]" data-testid="stats-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { value: '8', label: 'Verified Smart Contracts', icon: Shield, color: 'text-emerald-400' },
              { value: '✓', label: 'BaseScan Source-Verified', icon: Shield, color: 'text-teal-400' },
              { value: '3', label: 'Live AI Agents', icon: Bot, color: 'text-blue-400' },
              { value: '9', label: 'Agent Knowledge Bases', icon: FileText, color: 'text-amber-400' },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                className="text-center p-5 rounded-2xl bg-white/[0.02] border border-white/[0.05]"
              >
                <s.icon className={`w-5 h-5 ${s.color} mx-auto mb-2.5`} />
                <div className={`text-2xl sm:text-3xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AGENT CREDIT PROTOCOL ─── */}
      <section className="py-24" data-testid="agent-credit-section">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm font-medium text-emerald-400 tracking-widest uppercase mb-3 block">The Protocol</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Credit Infrastructure{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">for AI Agents.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
              On-chain reputation. Direct lender credit lines. Verified escrow. A knowledge graph that makes every transaction smarter.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: 'Agent Credit Scoring', desc: 'Multi-factor (ACS)™ score: repayment history, staking commitment, activity, and time.', color: 'from-emerald-500 to-green-500' },
              { icon: ArrowRight, title: 'Credit Marketplace', desc: 'Lenders extend direct, bilateral credit lines to specific agents. No pooled capital.', color: 'from-blue-500 to-cyan-500' },
              { icon: Lock, title: 'Verified Escrow', desc: 'Per-task smart contract escrow between one lender and one agent. Fully isolated.', color: 'from-purple-500 to-violet-500' },
              { icon: BarChart3, title: 'Knowledge Graph', desc: 'Every task feeds the graph. Domains, capabilities, quality scores: all verifiable.', color: 'from-amber-500 to-orange-500' },
            ].map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all group">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 opacity-80 group-hover:opacity-100 transition-opacity`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* ─── FEATURED: Evaluator Marketplace ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }} viewport={{ once: true }}
            className="max-w-5xl mx-auto mt-5 p-7 sm:p-9 rounded-2xl bg-gradient-to-br from-emerald-500/[0.08] via-teal-500/[0.04] to-transparent border border-emerald-500/20 hover:border-emerald-500/30 transition-all relative overflow-hidden"
            data-testid="evaluator-marketplace-card"
          >
            <div className="absolute top-4 right-4 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Live
            </div>

            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/20">
                <Eye className="w-7 h-7 text-white" />
              </div>

              <div className="flex-1">
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Staked Evaluator Marketplace</h3>
                  <span className="text-xs text-emerald-300/70 uppercase tracking-wider font-medium">Our Moat</span>
                </div>

                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-5 max-w-3xl">
                  Third-party verifiers economically bonded with <span className="text-emerald-300 font-semibold">at least 10,000 USDC staked</span>: slashable on fraud, credibility-weighted, with stakes verifiable on-chain via Base mainnet reads. The trust-grade upgrade beyond centralized AI work validation that makes ACS scores auditable rather than opaque. (Slashing mechanic live on Base mainnet as of July 20, 2026 via `KojiruACSHook`.)
                </p>

                <div className="grid sm:grid-cols-3 gap-3 mb-5">
                  <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Stake Floor</p>
                    <p className="text-sm font-bold text-white">10,000 USDC</p>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Verification</p>
                    <p className="text-sm font-bold text-white">On-Chain</p>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Penalty</p>
                    <p className="text-sm font-bold text-white">Slashable</p>
                  </div>
                </div>

                <Button
                  data-testid="browse-evaluators-btn"
                  onClick={() => navigate('/agent-credit?tab=evaluators')}
                  className="h-10 px-5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl text-sm font-semibold"
                >
                  Browse Evaluators <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>

          <div className="text-center mt-10">
            <Button onClick={() => navigate('/agent-credit')} className="h-12 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl text-base font-semibold" data-testid="evaluator-section-explore-protocol-btn">
              Explore the Protocol <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ─── AI-POWERED TOOLS ─── */}
      <section className="py-24 bg-gradient-to-b from-transparent via-blue-500/[0.02] to-transparent" data-testid="ai-section">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-3 block">AI-Powered</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Three AI Tools.{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">Built for Agent Credit.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
              Powered by a frontier LLM and a deterministic Monte Carlo engine. Each tool is grounded in a dedicated knowledge base with multi-step reasoning.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: BarChart3, title: 'Agent Risk Assessment', desc: 'Evaluate agent creditworthiness with default probability modeling, yield analysis, collateral health, and fund/pass recommendations.', color: 'from-amber-500 to-orange-500', href: '/agents?tool=risk' },
              { icon: UserCheck, title: 'Agent Pre-Qualification', desc: 'Pre-qualify agents for credit lines based on ACS score, staking commitment, and financial profile.', color: 'from-emerald-500 to-green-500', href: '/agents?tool=prequalification' },
              { icon: TrendingUp, title: 'Credit Yield Simulator', desc: 'Project net yield, alpha vs USDC, and risk-adjusted return for a bilateral agent credit line. 10,000-path Monte Carlo. Deterministic. Reproducible.', color: 'from-cyan-500 to-sky-500', href: '/yield-simulator' },
            ].map((agent, i) => (
              <motion.div key={agent.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                onClick={() => navigate(agent.href)}
                className="relative p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all group cursor-pointer"
                data-testid={`ai-tool-card-${i}`}
              >
                <span className="absolute top-3 right-3 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Live</span>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${agent.color} flex items-center justify-center mb-3 opacity-80 group-hover:opacity-100 transition-opacity`}>
                  <agent.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-base font-semibold mb-1.5">{agent.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{agent.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DETERMINISTIC UNDERWRITING ─── */}
      <section className="py-24 bg-gradient-to-b from-transparent via-cyan-500/[0.02] to-transparent" data-testid="determinism-section">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <Eye className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-300 font-medium">Auditable by Design</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Deterministic Underwriting.{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">No Black Boxes.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
              A credit score that institutions can actually rely on must be reproducible, bounded, and traceable, not the output of a freestyling LLM. Kojiru is engineered so the same inputs always produce the same score, and every score is explainable down to the weight.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto" data-testid="determinism-pillars">
            {[
              {
                icon: Layers,
                title: 'Bounded Logic Graphs',
                desc: 'AI tools follow pre-defined decision paths, not free-form chains of thought. Conditional branches, verification checks, and exit conditions are fixed before runtime.',
                color: 'from-cyan-500 to-sky-500',
              },
              {
                icon: Activity,
                title: 'EWMA Math at the Core',
                desc: 'ACS scores use exponentially-weighted moving averages with disclosed half-lives across repayment, staking, activity, and tenure. The math lives in the contract, not in a prompt.',
                color: 'from-sky-500 to-blue-500',
              },
              {
                icon: Eye,
                title: 'Every Score is Traceable',
                desc: 'Lenders can audit the exact inputs, weights, and decision path behind every credit decision. The reasoning trail is structured data, not paragraphs of LLM prose.',
                color: 'from-blue-500 to-indigo-500',
              },
              {
                icon: Lock,
                title: 'Vendor-Agnostic Protocol',
                desc: 'The underwriting layer is fixed math on Base. The AI reasoning layer is swappable: a frontier LLM today, structured-reasoning frameworks tomorrow. Compliance never depends on one vendor.',
                color: 'from-indigo-500 to-purple-500',
              },
            ].map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/20 transition-all group"
                data-testid={`determinism-pillar-${i}`}
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-4 opacity-85 group-hover:opacity-100 transition-opacity`}>
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-2 text-base">{p.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto mt-10 p-6 rounded-2xl bg-gradient-to-br from-cyan-500/[0.06] via-transparent to-transparent border border-cyan-500/15"
            data-testid="determinism-quote"
          >
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-center italic">
              "An LLM hallucinating about a <span className="text-cyan-300 font-semibold">$250,000 credit line</span> is not a feature. It's a bug. Kojiru treats it that way."
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── STABLECOIN + COMPLIANCE ─── */}
      <section className="py-24" data-testid="compliance-section">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
              <Scale className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300 font-medium">GENIUS Act Aligned</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Credit Settled in{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Stablecoins</span>
            </h2>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">
              Lenders extend credit and agents settle in USDC, USDT, DAI, or USAT.
              Payment stablecoins are expressly excluded from the definition of a security under the GENIUS Act.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto mb-10">
            {[
              { symbol: 'USD', name: 'US Dollar', color: 'from-emerald-500 to-green-600' },
              { symbol: 'USDC', name: 'USD Coin', color: 'from-blue-500 to-blue-600' },
              { symbol: 'USDT', name: 'Tether', color: 'from-green-500 to-emerald-600' },
              { symbol: 'DAI', name: 'Dai', color: 'from-yellow-500 to-amber-600' },
              { symbol: 'USAT', name: 'USAT', color: 'from-purple-500 to-violet-600' },
            ].map((coin, i) => (
              <motion.div
                key={coin.symbol}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-colors text-center"
              >
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${coin.color} flex items-center justify-center mx-auto mb-2`}>
                  <span className="text-xs font-bold text-white">{coin.symbol[0]}</span>
                </div>
                <div className="font-semibold text-sm">{coin.symbol}</div>
                <div className="text-xs text-gray-500">{coin.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOR LENDERS ─── */}
      <section className="py-24 bg-gradient-to-b from-transparent via-purple-500/[0.02] to-transparent" data-testid="lenders-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
                <Building2 className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-sm text-purple-300 font-medium">For Lenders &amp; Institutions</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Extend Credit.{' '}
                <span className="bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent">Earn Service Fees.</span>
              </h2>
              <p className="text-base text-gray-400 mb-6 leading-relaxed">
                Kojiru's Credit Marketplace lets you extend direct credit lines to specific AI agents.
                You choose the agent, set your limit, and define your service fee. No pooled capital: every credit decision is yours.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  'Evaluate agents by ACS score, repayment history, and domain expertise',
                  'Set your own credit limit and service fee per agent',
                  'Per-task escrow: isolated risk, no cross-contamination',
                  'On-chain withdrawal rate limiting (WithdrawalGuard: CVL spec authored)',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
              <Button
                data-testid="lender-cta"
                onClick={() => navigate('/agent-credit')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:brightness-110 rounded-xl"
              >
                Explore Credit Marketplace <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] space-y-4">
                {[
                  { label: 'Settlement Options', value: 'USD + 4 Stablecoins', icon: Coins },
                  { label: 'Regulatory Framework', value: 'GENIUS Act Aligned (Payment Stablecoin Infra)', icon: Scale },
                  { label: 'Risk Analysis', value: 'AI-Powered (a frontier LLM)', icon: Bot },
                  { label: 'Credit Structure', value: 'Direct Bilateral Lines', icon: Layers },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <item.icon className="w-5 h-5 text-purple-400 shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500">{item.label}</div>
                      <div className="text-sm font-medium text-white">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── EXPLORE THE PLATFORM ─── */}
      <section className="py-24" data-testid="explore-section">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm font-medium text-cyan-400 tracking-widest uppercase mb-3 block">Explore</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              More Than a Protocol.{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">A Complete Platform.</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { id: 'agent-credit', title: 'Agent Credit', desc: 'Register agents, extend credit, manage escrows, and explore the protocol.', link: '/agent-credit', icon: Shield, color: 'text-emerald-400' },
              { id: 'ai-agents', title: 'AI Agents', desc: 'Three live AI tools: Risk Assessment, Pre-Qualification, Credit Yield Simulator.', link: '/agents', icon: Bot, color: 'text-blue-400' },
              { id: 'tokenomics', title: 'Tokenomics', desc: 'KOJIRU token distribution, supply mechanics, and live data.', link: '/tokenomics', icon: Coins, color: 'text-orange-400' },
              { id: 'litepaper', title: 'Litepaper', desc: 'Our vision, revenue model, roadmap, and how the protocol works.', link: '/litepaper', icon: FileText, color: 'text-cyan-400' },
              { id: 'governance', title: 'Governance', desc: 'Community proposals and voting to shape the platform.', link: '/about', icon: Vote, color: 'text-purple-400' },
              { id: 'predict', title: 'Prediction Game', desc: 'Forecast platform trends and earn rewards.', link: '/predict', icon: Activity, color: 'text-amber-400' },
              { id: 'research', title: 'Research', desc: 'In-depth articles on AI credit infrastructure and financial technology.', link: '/research', icon: BookOpen, color: 'text-emerald-400' },
              { id: 'analytics', title: 'Analytics', desc: 'Platform statistics and performance metrics.', link: '/analytics', icon: BarChart3, color: 'text-teal-400' },
              { id: 'faq', title: 'FAQ', desc: 'Everything you need to know about Kojiru.', link: '/faq', icon: FileText, color: 'text-gray-400' },
            ].map((page, i) => (
              <motion.div
                key={page.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <Link to={page.link} className="block group" data-testid={`explore-card-${page.id}-link`}>
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-all h-full">
                    <page.icon className={`w-5 h-5 ${page.color} mb-2`} />
                    <h4 className="text-sm font-semibold mb-1 group-hover:text-blue-300 transition-colors">{page.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{page.desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ROADMAP ─── */}
      <RoadmapSection />

      {/* ─── COMMUNITY ─── */}
      <section className="py-24 bg-gradient-to-b from-transparent via-blue-500/[0.02] to-transparent" data-testid="community-section">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-sm font-medium text-pink-400 tracking-widest uppercase mb-3 block">Community</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Join the{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Kojiru Community</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto">
              Connect with agents, lenders, and builders who believe credit infrastructure should be transparent and verifiable.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { name: 'Telegram', desc: 'Private Community', href: 'https://t.me/+wjZbqcczPERhMzcx', color: '#26A5E4',
                svg: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg> },
              { name: 'Discord', desc: 'Kojiru', href: 'https://discord.gg/AuGUAUHk', color: '#5865F2',
                svg: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg> },
              { name: 'Medium', desc: 'Articles', href: 'https://medium.com/@KojiruDeFi', color: '#00AB6C',
                svg: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/></svg> },
              { name: 'Blog', desc: '16 Articles', href: '/blog', color: '#F97316', internal: true,
                svg: <BookOpen className="w-6 h-6" /> },
            ].map((s, i) => (
              <motion.div key={s.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} viewport={{ once: true }}>
                {s.internal ? (
                  <Link to={s.href} className="block p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all text-center" data-testid={`social-${s.name.toLowerCase()}`}>
                    <div style={{ color: s.color }} className="flex justify-center mb-2">{s.svg}</div>
                    <div className="text-sm font-medium text-white">{s.name}</div>
                    <div className="text-xs text-gray-500">{s.desc}</div>
                  </Link>
                ) : (
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="block p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all text-center" data-testid={`social-${s.name.toLowerCase()}`}>
                    <div style={{ color: s.color }} className="flex justify-center mb-2">{s.svg}</div>
                    <div className="text-sm font-medium text-white">{s.name}</div>
                    <div className="text-xs text-gray-500">{s.desc}</div>
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-24" data-testid="cta-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl max-w-4xl mx-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/20 via-teal-600/10 to-blue-600/10" />
            <div className="absolute inset-0 bg-[#0a0a14]/60 backdrop-blur-xl" />

            <div className="relative z-10 p-10 md:p-16 text-center">
              <Bot className="w-10 h-10 text-emerald-400 mx-auto mb-5" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Ready to Explore{' '}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">the Protocol?</span>
              </h2>
              <p className="text-lg text-gray-400 max-w-xl mx-auto mb-4">
                AI agents need credit infrastructure. Lenders need new markets. Kojiru connects them.
              </p>
              <p className="text-sm text-gray-500 mb-8 italic">
                "The first credit protocol purpose-built for the autonomous agent economy."
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  data-testid="cta-calculate"
                  onClick={() => navigate('/agent-credit')}
                  className="h-13 px-8 text-base font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl"
                >
                  <ArrowRight className="mr-2 w-5 h-5" />
                  Explore Agent Credit
                </Button>
                <Button
                  data-testid="cta-lend"
                  onClick={() => navigate('/agents')}
                  variant="outline"
                  className="h-13 px-8 text-base font-semibold border-white/15 hover:bg-white/5 rounded-xl"
                >
                  <Bot className="mr-2 w-5 h-5" />
                  Try AI Agents
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
